import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { isAuthError } from '../api/auth';
import { ApiError } from '../api/client';

interface SignInModalProps {
  show: boolean;
  onHide: () => void;
  onSignInSuccess: () => void;
}

const SignInModal: React.FC<SignInModalProps> = ({ show, onHide, onSignInSuccess }) => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleClose = () => {
    setFormData({ email: '', password: '' });
    setShowPassword(false);
    setError(null);
    onHide();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await login(formData.email, formData.password);
      onSignInSuccess();
      handleClose();
    } catch (err: unknown) {
      if (isAuthError(err)) {
        setError((err as ApiError).status === 401 ? 'Invalid email or password.' : 'Email already in use.');
      } else {
        setError('Sign in failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block site-modal" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content site-modal__content">
          <div className="modal-header site-modal__header">
            <div>
              <h5 className="modal-title site-modal__title mb-1">Sign In</h5>
              <p className="site-modal__subtitle mb-0">
                Authorized committee &amp; operational access only
              </p>
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={handleClose}
              disabled={isLoading}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body site-modal__body pb-2">
              {error ? (
                <div className="alert alert-danger py-2" role="alert">
                  {error}
                </div>
              ) : null}

              <div className="mb-3">
                <label htmlFor="sign-in-email" className="form-label">
                  Email Address
                </label>
                <div className="input-group">
                  <span className="input-group-text" aria-hidden="true">
                    <i className="bi bi-envelope" />
                  </span>
                  <input
                    type="email"
                    className="form-control"
                    id="sign-in-email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="name@example.com"
                    autoComplete="username"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="sign-in-password" className="form-label">
                  Password
                </label>
                <div className="input-group">
                  <span className="input-group-text" aria-hidden="true">
                    <i className="bi bi-lock" />
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    id="sign-in-password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary site-modal__toggle-password"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    disabled={isLoading}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-footer site-modal__footer">
              <button type="submit" className="btn site-modal__submit w-100" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignInModal;
